import axios from "axios";
import { IAction, IHost, IInstruction, ILog, IPartner, IPermission, IPlaybook, IPoint, IPop, IProblem, IQueue, IScheduleInterval, ISchedulePolicy, ISection, ITicket, ITicketMessage, IUser, IWaiting, IWhatsappSystem, IZabbix, IZabbixFilter, type ICompany, SelectFields } from "../../../../zabbix-shared/interfaces/intefaces";
import { backendUrl } from "../../../../zabbix-shared/env";
import { DeepPartial } from "../../../../zabbix-shared/interfaces/intefaces"
import 'vue3-toastify/dist/index.css';
import { toast } from 'vue3-toastify';

const api = axios.create({
  baseURL: backendUrl,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  handleApiError
);

type IParams<T> = DeepPartial<T> & {
  select?: SelectFields<T>
};

export interface ApiService<T> {
  create: (data: DeepPartial<T>) => Promise<T>;
  findAll: (params?: IParams<T>) => Promise<T[]>;
  findOne: (id: number, params?: IParams<T>) => Promise<T>;
  update: (id: number, data: DeepPartial<T>) => Promise<T>;
  delete: (id: number) => Promise<void>;
}

const createService = <T>(entity: string, displayName: string, showLogs: boolean = true): ApiService<T> => ({
  create: (data: DeepPartial<T>) => api.post<T>(`/${entity}`, data).then((res: any) => {
    if (showLogs) toast.success(`${displayName} criado(a) com sucesso!`, { autoClose: 2500 });
    return res.data;
  }),
  findAll: (params?: IParams<T>) => api.get<T[]>(`/${entity}/list`, { params }).then((res: any) => res.data),
  findOne: (id: number, params?: IParams<T>) => api.get<T>(`/${entity}/${id}`, { params }).then((res: any) => res.data),
  update: (id: number, data: DeepPartial<T>) => api.put<T>(`/${entity}/${id}`, data).then((res: any) => {
    if (showLogs) toast.success(`${displayName} atualizado(a) com sucesso!`, { autoClose: 2500 });
    return res.data;
  }),
  delete: (id: number) => api.delete(`/${entity}/${id}`).then(() => {
    if (showLogs) toast.success(`${displayName} deletado(a) com sucesso!`, { autoClose: 2500 });
  }),
});

export const CompanyService = createService<ICompany>("company", "Empresa");
export const PopService = createService<IPop>("pop", "POP");
export const HostService = createService<IHost>("host", "Host");
export const PartnerService = createService<IPartner>("partner", "Parceiro");
export const instructionService = createService<IInstruction>("instruction", "Instrução");
export const PointService = createService<IPoint>("point", "Ponto");
export const QueueService = createService<IQueue>("queue", "Fila");
export const TicketService = createService<ITicket>("ticket-config", "Configuração de Ticket");
export const logService = createService<ILog>("log", "Log", false);
export const whatsappSystemService = createService<IWhatsappSystem>("whatsapp-system", "Sistema WhatsApp");
export const userService = createService<IUser>("user", "Usuário");
export const permissionService = createService<IPermission>("permission", "Permissão");
export const actionService = createService<IAction>("action", "Ação");
export const playbookService = createService<IPlaybook>("playbook", "Playbook");
export const problemService = createService<IProblem>("problem", "Problema", false);
export const sectionService = createService<ISection>("section", "Seção");
export const zabbixService = createService<IZabbix>("zabbix", "Zabbix");
export const zabbixFilterService = createService<IZabbixFilter>("zabbix-filter", "Filtro Zabbix");
export const waitingService = createService<IWaiting>("waiting", "Fila de Espera");
export const ticketMessageService = createService<ITicketMessage>("ticket-message", "Mensagem do Ticket");
export const schedulePolicyService = createService<ISchedulePolicy>("schedule-policy", "Política de Agendamento");
export const scheduleIntervalsService = createService<IScheduleInterval>("schedule-interval", "Intervalo de Agendamento");

const apiNoHeader = axios.create({
  baseURL: backendUrl,
  withCredentials: true,
});

apiNoHeader.interceptors.response.use(
  (response) => response,
  handleApiError
);

export const TicketMetroService = {
  async open(data: any) {
    const response = await api.post('/ticket/open', data);
    return response.data;
  },
}

export const TicketCompanyService = {
  async open(data: any) {
    const response = await api.post('/ticket-company/open', data);
    return response.data;
  },
  async close(data: any) {
    const response = await api.post('/ticket-company/close', data);
    return response.data;
  },
  async addMessage(data: any) {
    const response = await api.post('/ticket-company/message', data);
    return response.data;
  }
}

export const fileService = {
  create: (data: { company: Partial<ICompany>, rawFiles: File[], ticketMessageId?: number }) => {
    const formData = new FormData();

    formData.append('company', JSON.stringify(data.company));
    if (data.ticketMessageId) {
      formData.append('ticketMessageId', data.ticketMessageId.toString());
    }
    data.rawFiles.forEach(file => {
      formData.append('files', file);
    });

    return apiNoHeader.post('/file/upload', formData).then(res => res.data);
  },
  findOne: async (id: number) => {
    const response = await apiNoHeader.get(`/file/${id}`, { responseType: 'blob' });
    return response.data;
  }
}


export async function getProblemsWithRelations(data: any) {
  const response = await api.get(`/problem-list-associeted`, { params: data });
  return response.data;
}
export async function executePlaybook(id: number, body?: any) {
  const response = await api.post(`/playbook/execute/${id}`, body);
  return response.data;
}

export async function messageTicket(messageData: any) {
  const response = await api.post(`/ticket/message`, messageData);
  return response.data;
}

export async function closeTicket(messageData: any) {
  const response = await api.post(`/ticket/close`, messageData);
  return response.data;
}

export async function sendWhatsappMessage(message: string, phoneNumber: string, eventId: number, zabbixId: number) {
  const data = {
    number: phoneNumber,
    body: message,
    eventId: eventId,
    zabbixId: zabbixId
  };

  try {
    await api.post(
      `${backendUrl}/whatsapp/send/metro`,
      data,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
  } catch (error) {
    toast.error("Falha no envio de mensagem do whatsapp")
  }
}

export default api;

function handleApiError(error: any) {
  // if (error.response && error.response.status === 503) {
  //   toast.error('Erro de conexão com o servidor. Por favor, tente novamente mais tarde.', {
  //     autoClose: 3500,
  //   });
  // } else if (!error.response || error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
  //   toast.error('Não foi possível conectar ao servidor', {
  //     autoClose: 3500,
  //   });
  // }
  return Promise.reject(error);
}
